import { useEffect, useRef, useState } from 'react';
import { MarketStatus } from '@sportspredict/shared';
import { TABS, MARKET_SECTIONS } from '@/constants';

export function useSettledAndAutoTradeTracking({
	groups,
	isSurvivorMode,
	isCrowdLockMode,
	user,
	autoTradeToShow,
	setAutoTradeToShow,
	tradesByMarket,
	activeTab,
	activeMarketSection,
	setActiveMarketSection,
	marketsLoaded,
}: {
	groups: any;
	isSurvivorMode: boolean;
	isCrowdLockMode: boolean;
	user: any;
	autoTradeToShow: any;
	setAutoTradeToShow: (value: any) => void;
	tradesByMarket: any;
	activeTab: any;
	activeMarketSection: any;
	setActiveMarketSection: (value: any) => void;
	marketsLoaded: boolean;
}) {
	const [recentlyTradedState, setRecentlyTradedState] = useState<any[]>([]);
	const [settledState, setSettledState] = useState<any[]>([]);
	useEffect(() => {
		if (!isSurvivorMode) return;

		setRecentlyTradedState(groups.recentlyTraded);
		setSettledState(groups.settled);

		const timers: any[] = [];

		groups.recentlyTraded.forEach(({ market, trade }: any) => {
			const settledTimestamp = new Date(market.settled_date).getTime();
			const now = Date.now();
			if (market.status !== MarketStatus.Settled) return;
			const delay = Math.max(0, 10000 - (now - settledTimestamp));

			const timer = setTimeout(() => {
				setRecentlyTradedState((prev) => prev.filter((i: any) => i.trade.id !== trade.id));

				setSettledState((prev) => [...prev, { market, trade }]);
			}, delay);

			timers.push(timer);
		});

		return () => timers.forEach(clearTimeout);
	}, [groups.recentlyTraded, groups.settled, isSurvivorMode]);

	const shownAutoTradesRef = useRef(new Set());

	useEffect(() => {
		if (!isCrowdLockMode) return;
		if (!user?.id) return;

		if (autoTradeToShow?.tradeId) return;

		for (const [marketId, trades] of Object.entries(tradesByMarket ?? {})) {
			if (!Array.isArray(trades) || trades.length === 0) continue;

			const mineAuto = (trades as any[]).find(
				(t: any) =>
					String(t?.user_id) === String(user.id) &&
					t?.isAutoTrade === true &&
					!shownAutoTradesRef.current.has(t?.id)
			);

			if (mineAuto) {
				shownAutoTradesRef.current.add(mineAuto.id);
				setAutoTradeToShow({ tradeId: mineAuto.id, marketId });
				break;
			}
		}
	}, [isCrowdLockMode, user?.id, tradesByMarket, autoTradeToShow?.tradeId]);

	const didSwitchOnMount = useRef(false);

	useEffect(() => {
		if (isCrowdLockMode) return;
		if (activeTab !== TABS.MARKETS) return;
		if (didSwitchOnMount.current) return;
		if (!marketsLoaded) return;

		const shouldSwitch = groups.openForPredictions.length === 0;

		if (shouldSwitch && activeMarketSection !== MARKET_SECTIONS.MyPicks) {
			setActiveMarketSection(MARKET_SECTIONS.MyPicks);
		}

		didSwitchOnMount.current = true;
	}, [activeTab, groups, marketsLoaded]);

	return { recentlyTradedState, settledState };
}
